/**
 * Custom Select Component
 *
 * Transforms native <select> elements into fully accessible custom dropdowns.
 * Maintains sync with native select for proper form submission and accessibility.
 *
 * Features:
 * - Full keyboard navigation (Arrow keys, Home/End, Enter, Space, Escape)
 * - Typeahead search (type letters to jump to options)
 * - ARIA-compliant with screen reader support
 * - Syncs with native select value changes and form resets
 * - Disabled state support with mutation observer
 * - Optional height animation with GSAP
 * - Modal scroll prevention when open
 * - Click outside to close
 * - Auto-unwraps Webflow component slot divs
 *
 * HTML Structure:
 *   <div data-hs-form-select="wrapper">
 *     <label>Choose option</label>
 *     <select name="example">
 *       <option value="">Select...</option>
 *       <option value="1">Option 1</option>
 *     </select>
 *     <button data-hs-form-select="button">
 *       <span>Placeholder text</span>
 *     </button>
 *     <div data-hs-height="element">
 *       <div data-hs-form-select="list">
 *         <div role="option"><span>Template</span></div>
 *       </div>
 *     </div>
 *   </div>
 *
 * Keyboard Controls:
 *   - Space/Enter (button): Open dropdown
 *   - Arrow Up/Down: Navigate options
 *   - Home/End: Jump to first/last option
 *   - Escape: Close dropdown
 *   - Type letters: Typeahead search
 *
 * Accessibility:
 *   - Native select hidden but kept for form submission
 *   - Custom UI uses role="combobox" and role="listbox"
 *   - aria-expanded, aria-selected, aria-activedescendant
 *   - Label properly connected to native select
 */

import {
  querySelectorAll,
  querySelector,
  animateHeight,
  setHeight,
  openModal,
  closeModal,
} from '@utils';

export function init(config) {
  // Centralized cleanup tracking
  const cleanup = {
    observers: [] as MutationObserver[],
    handlers: [] as Array<{
      element: Element | Document;
      event: string;
      handler: EventListener;
      options?: boolean | AddEventListenerOptions;
    }>,
    timeouts: [] as number[],
    domContentLoadedHandler: null as (() => void) | null,
  };

  const addObserver = (observer: MutationObserver) => cleanup.observers.push(observer);
  const addHandler = (
    element: Element | Document,
    event: string,
    handler: EventListener,
    options?: boolean | AddEventListenerOptions
  ) => {
    element.addEventListener(event, handler, options);
    cleanup.handlers.push({ element, event, handler, options });
  };
  const addTimeout = (timeoutId: number) => cleanup.timeouts.push(timeoutId);

  // Initialize all custom selects on the page
  function initCustomSelects() {
    // Unwrap any divs inside select elements (Webflow component slots)
    document.querySelectorAll('select > div').forEach((div) => {
      const select = div.parentElement;
      while (div.firstChild) {
        select.appendChild(div.firstChild);
      }
      div.remove();
    });

    const selectWrappers = querySelectorAll(config, 'wrapper');

    selectWrappers.forEach((wrapper) => {
      initSingleSelect(wrapper, addHandler, addObserver, addTimeout);
    });
  }

  // Initialize a single custom select
  function initSingleSelect(
    wrapper: Element,
    addHandler: (
      element: Element | Document,
      event: string,
      handler: EventListener,
      options?: boolean | AddEventListenerOptions
    ) => void,
    addObserver: (observer: MutationObserver) => void,
    addTimeout: (timeoutId: number) => void
  ) {
    // Find all required elements
    const realSelect = wrapper.querySelector('select') as HTMLSelectElement;
    if (!realSelect) {
      console.warn('[select] Missing <select> element in custom select wrapper', wrapper);
      return;
    }

    // Hide real select from screen readers since custom UI provides accessible alternative
    realSelect.setAttribute('aria-hidden', 'true');
    realSelect.setAttribute('tabindex', '-1');

    const selectName = realSelect.getAttribute('name') || 'custom-select';
    const customList = querySelector(config, 'list', wrapper) as HTMLElement;
    const button = querySelector(config, 'button', wrapper) as HTMLButtonElement;

    if (!customList || !button) {
      console.warn(
        '[select] Missing required elements (list or button) in custom select wrapper',
        wrapper
      );
      return;
    }

    // Sync disabled state from native select
    function updateDisabledState() {
      if (realSelect.disabled) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      }
    }
    updateDisabledState();

    // Get and clone the option template
    const optionTemplate = customList.firstElementChild;
    if (!optionTemplate) {
      console.warn('[select] Missing option template in custom select list', wrapper);
      return;
    }

    const templateClone = optionTemplate.cloneNode(true);
    optionTemplate.remove();

    // Build options from real select
    const realOptions = realSelect.querySelectorAll('option');
    realOptions.forEach((option: HTMLOptionElement, index: number) => {
      const optionElement = templateClone.cloneNode(true) as HTMLElement;
      const textSpan = optionElement.querySelector('span');

      if (textSpan) {
        textSpan.textContent = option.textContent;
      }

      // Add attributes
      optionElement.setAttribute('data-value', option.value);
      optionElement.setAttribute('role', 'option');
      optionElement.setAttribute('id', `${selectName}-option-${index}`);
      optionElement.setAttribute('tabindex', '-1');

      // Set selected state if this option is selected
      if (option.selected) {
        optionElement.setAttribute('aria-selected', 'true');
        // Update button text
        const buttonText = button.querySelector('span') || button;
        if (buttonText.tagName === 'SPAN') {
          buttonText.textContent = option.textContent;
        }
        // Set initial is-filled state based on value
        if (option.value) {
          button.classList.add('is-filled');
        } else {
          button.classList.remove('is-filled');
        }
      } else {
        optionElement.setAttribute('aria-selected', 'false');
      }

      customList.appendChild(optionElement);
    });

    // Add ARIA attributes
    customList.setAttribute('role', 'listbox');
    customList.setAttribute('id', `${selectName}-listbox`);
    customList.setAttribute('tabindex', '-1');
    customList.setAttribute('aria-hidden', 'true'); // Initial state: hidden
    customList.inert = true; // Prevent keyboard/mouse interaction when closed

    button.setAttribute('role', 'combobox');
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-controls', `${selectName}-listbox`);
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('id', `${selectName}-button`);

    // Find and connect label if exists
    const label =
      wrapper.querySelector('label') || document.querySelector(`label[for="${realSelect.id}"]`);
    if (label) {
      const labelId = label.id || `${selectName}-label`;
      label.id = labelId;
      // Ensure real select has proper ID for label connection
      if (!realSelect.id) {
        realSelect.id = `${selectName}-select`;
      }
      label.setAttribute('for', realSelect.id);
      button.setAttribute('aria-labelledby', labelId);
    }

    // Check for visual height wrapper (optional)
    // Only animate if data-hs-height="element" exists in wrapper
    const heightElement = wrapper.querySelector('[data-hs-height="element"]');
    const animationTarget = heightElement || null;

    // Track state
    let searchString = '';
    let searchTimeout: number | null = null;
    let enterPressed = false;

    // Function to check if select is open
    function isSelectOpen() {
      return wrapper.classList.contains('is-active');
    }

    // Update ARIA states based on current visual state
    function updateARIAStates() {
      const isCurrentlyOpen = isSelectOpen();
      const wasOpen = button.getAttribute('aria-expanded') === 'true';

      // If closing and focus is inside list, return focus first
      if (wasOpen && !isCurrentlyOpen && customList.contains(document.activeElement)) {
        button.focus();
      }

      // Update ARIA attributes
      button.setAttribute('aria-expanded', isCurrentlyOpen ? 'true' : 'false');
      customList.setAttribute('aria-hidden', isCurrentlyOpen ? 'false' : 'true');
      customList.inert = !isCurrentlyOpen; // Enable/disable interaction

      if (isCurrentlyOpen) {
        // Make all options tabbable when opened
        const options = customList.querySelectorAll('[role="option"]');
        options.forEach((opt) => {
          opt.setAttribute('tabindex', '0');
        });

        // Register with modal manager to prevent body scroll (only when opening)
        if (!wasOpen) {
          openModal();
        }
      } else {
        // Reset on close
        button.removeAttribute('aria-activedescendant');
        const options = customList.querySelectorAll('[role="option"]');
        options.forEach((opt) => {
          opt.setAttribute('tabindex', '-1');
        });

        // Unregister with modal manager to restore body scroll (only when closing)
        if (wasOpen) {
          closeModal();
        }
      }

      // Animate height if configured
      if (animationTarget) {
        animateHeight(animationTarget, isCurrentlyOpen, { duration: 300, ease: 'power2.inOut' });
      }
    }

    // Set initial height without animation (if configured)
    if (animationTarget) {
      setHeight(animationTarget, isSelectOpen());
    }

    // Set initial ARIA states
    updateARIAStates();

    // Focus option by index
    function focusOption(index: number) {
      const options = customList.querySelectorAll('[role="option"]') as NodeListOf<HTMLElement>;
      if (index < 0 || index >= options.length) return;

      // Remove previous focus
      options.forEach((opt) => {
        opt.classList.remove('focused');
        opt.setAttribute('tabindex', '-1');
      });

      // Add new focus
      options[index].classList.add('focused');
      options[index].setAttribute('tabindex', '0');
      options[index].focus();
      button.setAttribute('aria-activedescendant', options[index].id);
    }

    // Typeahead search functionality
    function handleTypeahead(key: string) {
      // Only handle printable characters (single character keys)
      if (key.length !== 1) return false;

      // Clear existing timeout
      if (searchTimeout !== null) {
        clearTimeout(searchTimeout);
        // Remove from cleanup tracking
        const index = cleanup.timeouts.indexOf(searchTimeout);
        if (index > -1) cleanup.timeouts.splice(index, 1);
      }

      // Get all options
      const options = Array.from(customList.querySelectorAll('[role="option"]'));
      if (options.length === 0) return false;

      // Append to search string
      searchString += key.toLowerCase();

      // Find currently focused option index
      const currentIndex = options.findIndex((opt) => opt.classList.contains('focused'));

      // If repeating single letter, cycle through options starting with that letter
      if (
        searchString.length === 1 ||
        searchString.split('').every((char) => char === searchString[0])
      ) {
        const letter = searchString[0];
        let startIndex = currentIndex + 1;

        // Search from next option onwards
        for (let i = 0; i < options.length; i++) {
          const index = (startIndex + i) % options.length;
          const text = (
            options[index].querySelector('span')?.textContent || options[index].textContent
          ).toLowerCase();
          if (text.startsWith(letter)) {
            focusOption(index);
            if (!isSelectOpen()) wrapper.classList.add('is-active'); // Open if closed
            searchTimeout = setTimeout(clearSearch, 500);
            addTimeout(searchTimeout);
            return true;
          }
        }
      } else {
        // Multi-character search - find first match starting with search string
        const matchIndex = options.findIndex((opt) => {
          const text = (opt.querySelector('span')?.textContent || opt.textContent).toLowerCase();
          return text.startsWith(searchString);
        });

        if (matchIndex !== -1) {
          focusOption(matchIndex);
          if (!isSelectOpen()) wrapper.classList.add('is-active'); // Open if closed
          searchTimeout = setTimeout(clearSearch, 500);
          addTimeout(searchTimeout);
          return true;
        }
      }

      // Reset timeout even if no match found
      searchTimeout = setTimeout(clearSearch, 500);
      addTimeout(searchTimeout);
      return false;
    }

    // Clear search string
    function clearSearch() {
      searchString = '';
      searchTimeout = null;
    }

    // Select option
    function selectOption(optionElement: HTMLElement) {
      const value = optionElement.getAttribute('data-value') || '';
      const text = optionElement.querySelector('span')?.textContent || optionElement.textContent;

      // Update real select
      realSelect.value = value;
      realSelect.dispatchEvent(new Event('change', { bubbles: true }));

      // Update button text
      const buttonText = button.querySelector('span') || button;
      if (buttonText.tagName === 'SPAN') {
        buttonText.textContent = text;
      }

      // Update is-filled class based on value
      if (value) {
        button.classList.add('is-filled');
      } else {
        button.classList.remove('is-filled');
      }

      // Update aria-selected
      customList.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      optionElement.setAttribute('aria-selected', 'true');

      // Close dropdown by toggling wrapper
      wrapper.classList.remove('is-active');
    }

    // Button click handler
    const buttonClickHandler = (e: Event) => {
      // If Enter was pressed, don't toggle - allow form submission
      if (enterPressed) {
        enterPressed = false;
        return;
      }
      e.preventDefault();
      wrapper.classList.toggle('is-active');
    };
    addHandler(button, 'click', buttonClickHandler);

    // Button keyboard events
    const buttonKeydownHandler = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          // Find the form's submit button and click it
          const formElement = button.closest('form');
          if (formElement) {
            const submitButton = (formElement.querySelector(
              'button[type="submit"], input[type="submit"]'
            ) || formElement.querySelector('button:not([type])')) as HTMLElement | null;
            if (submitButton) {
              submitButton.click();
            }
          }
          break;

        case ' ':
          e.preventDefault();
          wrapper.classList.toggle('is-active');
          break;

        case 'ArrowDown':
          e.preventDefault();
          if (!isSelectOpen()) {
            wrapper.classList.add('is-active');
            // Wait for dropdown to open, then focus first option
            requestAnimationFrame(() => {
              focusOption(0);
            });
          } else {
            focusOption(0);
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (!isSelectOpen()) {
            wrapper.classList.add('is-active');
            // Wait for dropdown to open, then focus first option (matches native)
            requestAnimationFrame(() => {
              focusOption(0);
            });
          } else {
            const options = customList.querySelectorAll('[role="option"]');
            focusOption(options.length - 1);
          }
          break;

        case 'Escape':
          if (isSelectOpen()) {
            e.preventDefault();
            wrapper.classList.remove('is-active');
          }
          break;

        case 'Home':
          e.preventDefault();
          if (!isSelectOpen()) {
            wrapper.classList.add('is-active');
          }
          focusOption(0);
          break;

        case 'End':
          e.preventDefault();
          if (!isSelectOpen()) {
            wrapper.classList.add('is-active');
          }
          const endOptions = customList.querySelectorAll('[role="option"]');
          focusOption(endOptions.length - 1);
          break;

        default:
          // Handle typeahead search
          if (handleTypeahead(e.key)) {
            e.preventDefault();
          }
          break;
      }
    };
    addHandler(button, 'keydown', buttonKeydownHandler);

    // Option keyboard events (delegated)
    const listKeydownHandler = (e: KeyboardEvent) => {
      const option = (e.target as Element).closest('[role="option"]') as HTMLElement | null;
      if (!option) return;

      const options = Array.from(customList.querySelectorAll('[role="option"]'));
      const currentIdx = options.indexOf(option);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (currentIdx < options.length - 1) {
            focusOption(currentIdx + 1);
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (currentIdx > 0) {
            focusOption(currentIdx - 1);
          }
          // Stay on first option if already there (matches native)
          break;

        case 'Enter':
          e.preventDefault();
          selectOption(option);
          break;

        case 'Escape':
          e.preventDefault();
          wrapper.classList.remove('is-active');
          button.focus();
          break;

        case 'Home':
          e.preventDefault();
          focusOption(0);
          break;

        case 'End':
          e.preventDefault();
          focusOption(options.length - 1);
          break;

        default:
          // Handle typeahead search
          if (handleTypeahead(e.key)) {
            e.preventDefault();
          }
          break;
      }
    };
    addHandler(customList, 'keydown', listKeydownHandler);

    // Option click events
    const listClickHandler = (e: MouseEvent) => {
      const option = (e.target as Element).closest('[role="option"]') as HTMLElement | null;
      if (option) {
        selectOption(option);
      }
    };
    addHandler(customList, 'click', listClickHandler);

    // Handle tabbing out of select list
    const listFocusoutHandler = (e: FocusEvent) => {
      // Check if focus is moving outside the wrapper (or to browser chrome)
      if (
        (!e.relatedTarget || !wrapper.contains(e.relatedTarget as Node)) &&
        wrapper.classList.contains('is-active')
      ) {
        wrapper.classList.remove('is-active'); // Close the dropdown
      }
    };
    addHandler(customList, 'focusout', listFocusoutHandler);

    // Handle clicks outside to close dropdown
    const documentClickHandler = (e: MouseEvent) => {
      // If dropdown is open and click is outside wrapper, close it
      if (wrapper.classList.contains('is-active') && !wrapper.contains(e.target as Node)) {
        wrapper.classList.remove('is-active');
      }
    };
    addHandler(document, 'click', documentClickHandler);

    // Monitor for class changes on wrapper and update ARIA states
    const observer = new MutationObserver(() => {
      updateARIAStates();
    });

    observer.observe(wrapper, {
      attributes: true,
      attributeFilter: ['class'],
    });
    addObserver(observer);

    // Observe native select for disabled attribute changes
    const disabledObserver = new MutationObserver(() => {
      updateDisabledState();
    });
    disabledObserver.observe(realSelect, {
      attributes: true,
      attributeFilter: ['disabled'],
    });
    addObserver(disabledObserver);

    // Sync with real select changes
    const selectChangeHandler = () => {
      const selectedOption = realSelect.options[realSelect.selectedIndex];
      if (selectedOption) {
        const options = customList.querySelectorAll('[role="option"]');
        const customOption = Array.from(options).find(
          (opt) => opt.getAttribute('data-value') === selectedOption.value
        );
        if (customOption) {
          // Update button text
          const text = customOption.querySelector('span')?.textContent || customOption.textContent;
          const buttonText = button.querySelector('span') || button;
          if (buttonText.tagName === 'SPAN') {
            buttonText.textContent = text;
          }

          // Update is-filled class based on value
          if (selectedOption.value) {
            button.classList.add('is-filled');
          } else {
            button.classList.remove('is-filled');
          }

          // Update aria-selected
          options.forEach((opt) => {
            opt.setAttribute('aria-selected', 'false');
          });
          customOption.setAttribute('aria-selected', 'true');
        }
      }
    };
    addHandler(realSelect, 'change', selectChangeHandler);
    addHandler(realSelect, 'input', selectChangeHandler);

    // Handle form reset
    const form = realSelect.closest('form') as HTMLFormElement | null;
    if (form) {
      const formResetHandler = () => {
        // Native select resets on next tick
        setTimeout(() => {
          selectChangeHandler();
        }, 0);
      };
      addHandler(form, 'reset', formResetHandler);
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    cleanup.domContentLoadedHandler = initCustomSelects;
    document.addEventListener('DOMContentLoaded', cleanup.domContentLoadedHandler);
  } else {
    initCustomSelects();
  }

  // INTERNAL API: Exposed for dynamic content reinitialization
  // WARNING: This is an internal API and may change without notice
  // Use window.hsmain.form.reinit() instead (if available in future versions)
  (window as any).initCustomSelects = initCustomSelects;

  return {
    result: 'select initialized',
    destroy: () => {
      // Remove DOMContentLoaded listener
      if (cleanup.domContentLoadedHandler) {
        document.removeEventListener('DOMContentLoaded', cleanup.domContentLoadedHandler);
        cleanup.domContentLoadedHandler = null;
      }

      // Disconnect all observers
      cleanup.observers.forEach((obs) => obs.disconnect());
      cleanup.observers.length = 0;

      // Clear all timeouts
      cleanup.timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      cleanup.timeouts.length = 0;

      // Remove all event listeners
      cleanup.handlers.forEach(({ element, event, handler, options }) => {
        element.removeEventListener(event, handler, options);
      });
      cleanup.handlers.length = 0;

      // Remove window API
      if ((window as any).initCustomSelects) {
        delete (window as any).initCustomSelects;
      }
    },
  };
}
